import React, { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Knowledge Base - Frequently Asked Questions
 */
const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqCategories = [
    {
      category: "General",
      questions: [
        {
          q: "What is Videxa?",
          a: "Videxa is an AI-powered healthcare platform that helps organizations optimize their revenue cycle, improve clinical documentation, and streamline operations. We provide tools for claims management, denial prevention, analytics, and more."
        },
        {
          q: "Who is Videxa designed for?",
          a: "Videxa is designed for hospitals, health systems, physician practices, and healthcare organizations of all sizes. Our solutions serve revenue cycle teams, CDI specialists, coders, billing staff, and healthcare executives."
        },
        {
          q: "Is Videxa HIPAA compliant?",
          a: "Yes, Videxa is fully HIPAA compliant. We maintain SOC 2 Type II certification and implement comprehensive security measures including encryption, access controls, and audit logging to protect patient health information."
        },
        {
          q: "Does Videxa integrate with my EHR?",
          a: "Yes, Videxa integrates with all major EHR systems including Epic, Cerner, MEDITECH, Allscripts, athenahealth, and many others. We also support HL7 FHIR standards for additional integration flexibility."
        }
      ]
    },
    {
      category: "Pricing & Plans",
      questions: [
        {
          q: "How much does Videxa cost?",
          a: "Videxa offers flexible pricing based on your organization's size and needs. We offer subscription plans that can be customized to include the features you need. Contact our sales team for a personalized quote."
        },
        {
          q: "Is there a free trial?",
          a: "Yes, we offer a free CDI trial that allows you to experience our platform with your own data. The trial includes access to our CDI query assistant and analytics features."
        },
        {
          q: "What's included in the subscription?",
          a: "All subscriptions include access to the core platform, standard integrations, customer support, and regular updates. Premium features like advanced analytics, custom integrations, and dedicated support are available in higher-tier plans."
        },
        {
          q: "Can I cancel my subscription?",
          a: "Yes, you can cancel your subscription at any time. We offer monthly and annual billing options. Annual subscriptions include a discount but are billed upfront."
        }
      ]
    },
    {
      category: "Features & Functionality",
      questions: [
        {
          q: "What is the CDI Query Assistant?",
          a: "The CDI Query Assistant is an AI-powered tool that helps Clinical Documentation Improvement specialists identify documentation gaps, generate queries, and improve clinical documentation accuracy for better coding and reimbursement."
        },
        {
          q: "How does denial prevention work?",
          a: "Our AI analyzes claims before submission to identify potential issues that could lead to denials. It checks for coding errors, missing documentation, authorization requirements, and payer-specific rules, alerting you to fix issues before submission."
        },
        {
          q: "Can I customize reports and dashboards?",
          a: "Yes, Videxa offers customizable reports and dashboards. You can create custom views, set up scheduled reports, and configure alerts based on your specific KPIs and requirements."
        },
        {
          q: "Does Videxa support prior authorization?",
          a: "Yes, Videxa includes prior authorization automation that helps submit authorization requests, track status, and manage the approval process with payers."
        }
      ]
    },
    {
      category: "Implementation & Support",
      questions: [
        {
          q: "How long does implementation take?",
          a: "Implementation typically takes 2-4 weeks for standard deployments. This includes system configuration, integration setup, data migration, and user training. Complex implementations with multiple integrations may take longer."
        },
        {
          q: "Do you provide training?",
          a: "Yes, we provide comprehensive training for all users. This includes live training sessions, on-demand video tutorials, documentation, and ongoing support. We also offer train-the-trainer programs for larger organizations."
        },
        {
          q: "What support is available?",
          a: "All customers have access to email and chat support during business hours. Premium support with 24/7 phone support, dedicated account managers, and faster response times is available with higher-tier plans."
        },
        {
          q: "Can you help with data migration?",
          a: "Yes, our implementation team can assist with migrating your existing data to Videxa. We support various data formats and provide tools for data validation and mapping."
        }
      ]
    },
    {
      category: "Security & Compliance",
      questions: [
        {
          q: "How is my data protected?",
          a: "We use industry-standard encryption (AES-256) for data at rest and TLS 1.3 for data in transit. Access is controlled through role-based permissions and multi-factor authentication. All access is logged for audit purposes."
        },
        {
          q: "Where is data stored?",
          a: "Data is stored in secure, HIPAA-compliant data centers in the United States. We use redundant storage and regular backups to ensure data availability and disaster recovery."
        },
        {
          q: "Do you sign BAAs?",
          a: "Yes, we execute Business Associate Agreements (BAAs) with all customers as required by HIPAA. Contact our team to request a BAA."
        },
        {
          q: "How do you handle data breaches?",
          a: "We have comprehensive incident response procedures in place. In the unlikely event of a breach, we follow all regulatory notification requirements and work with affected customers to address any concerns."
        }
      ]
    }
  ];

  const toggleQuestion = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <div className="faq-page">
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-[#0E3056] to-[#1a4a7a] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/knowledge-base" className="text-blue-300 hover:text-white mb-4 inline-block">
              ← Back to Knowledge Base
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Frequently Asked Questions</h1>
            <p className="text-xl text-gray-200">
              Find answers to common questions about Videxa.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {faqCategories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {category.category}
                </h2>
                <div className="space-y-3">
                  {category.questions.map((item, questionIndex) => {
                    const key = `${categoryIndex}-${questionIndex}`;
                    const isOpen = openIndex === key;
                    return (
                      <div
                        key={questionIndex}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden"
                      >
                        <button
                          onClick={() => toggleQuestion(categoryIndex, questionIndex)}
                          className="w-full p-4 text-left flex justify-between items-center hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        >
                          <span className="font-medium text-gray-900 dark:text-white pr-4">
                            {item.q}
                          </span>
                          <span className={`flex-shrink-0 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                            ▼
                          </span>
                        </button>
                        {isOpen && (
                          <div className="p-4 pt-0 border-t border-gray-200 dark:border-gray-600">
                            <p className="text-gray-600 dark:text-gray-300">
                              {item.a}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">
            Still Have Questions?
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            Can't find the answer you're looking for? Our team is happy to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-block bg-[#0E3056] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#1a4a7a] transition-colors"
            >
              Contact Us
            </Link>
            <Link
              to="/cdi-trial"
              className="inline-block border-2 border-[#0E3056] text-[#0E3056] dark:border-[#FFC000] dark:text-[#FFC000] px-8 py-3 rounded-lg font-semibold hover:bg-[#0E3056] hover:text-white dark:hover:bg-[#FFC000] dark:hover:text-gray-900 transition-colors"
            >
              Start Free Trial
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
